<template>
  <CustomizationFunnel
    :is-services-pickers-response-pending
    :pickers="pickersResponse"
    :pickers-services="pickersServices?.pickerGroups ?? []"
    :price
    :price-without-subsidies
    :price-without-trade-in
    :product
    :product-tracking
    :selected-mobile-plan
    :selected-offer
    :upgrade-picker-fields="upgradeFields"
  >
    <template #sticky-bar="{ currentStep, totalSteps, selectedOptions }">
      <RevPortal defer :to="`#${STICKY_BARS_PORTAL_TARGET}`">
        <StickyBarTop
          :price
          :price-without-subsidies
          :product
          :selected-mobile-plan
          :selected-offer
          :selected-options
          :telco-selection="telcoCartSelection"
          :tracking="productTracking"
          :upgrade-picker-fields="upgradeFields"
        >
          <template #below-bar>
            <DealBanner
              v-if="selectedOffer.price && deal"
              class="absolute top-full inset-x-0"
              :deal
              :price="selectedOffer.price"
            />
            <FreeShippingBanner
              v-else-if="shouldDisplayFreeShippingBanner"
              class="absolute top-full inset-x-0"
            />
          </template>

          <template #progress-bar>
            <ProgressBar :current-step :total-steps />
          </template>
        </StickyBarTop>

        <StickyBarTopSmall
          :select="selectedOffer.shipping"
          :selected-options
          :shipping="selectedOffer.shipping"
          :upgrade-picker-fields="upgradeFields"
        >
          <template #progress-bar>
            <ProgressBar :current-step :total-steps />
          </template>
        </StickyBarTopSmall>
        <ClientOnly>
          <UpgradeForLess
            :pickers="pickersResponse"
            :product
            @upgrade-accepted="handleUpgradeAccepted"
          />
        </ClientOnly>
      </RevPortal>
    </template>
    <template #details>
      <div>
        <Accessories
          :comes-with="product.comesWith"
          :partner-promo-codes="product.includedServiceOffers.partnerPromoCodes"
        />

        <!-- TODO after we roll out M&M on all cats -->
        <!-- Should be above reassurance items in the proposal or after reassurance when no pickers -->
        <SellerComment
          v-if="selectedOffer && selectedOffer.sellerComment"
          :comment="selectedOffer.sellerComment"
        />
      </div>
    </template>
    <template v-if="$slots['guidance-grades']" #guidance-grades="slotProps">
      <slot name="guidance-grades" v-bind="slotProps" />
    </template>
  </CustomizationFunnel>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import type {
  GetLegacyServicePickersResponse,
  GetPickersResponse,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-partnerships/mobile-plan/types/mobile-plan-offers'
import DealBanner from '@backmarket/nuxt-layer-deals/DealBanner.vue'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { RevPortal } from '@ds/components/Portal'

import { ClientOnly } from '#components'

import StickyBarTop from '~/scopes/product-core/components/presentationals//CustomizationFunnel/components/StickyBarTop/StickyBarTop.vue'
import StickyBarTopSmall from '~/scopes/product-core/components/presentationals//CustomizationFunnel/components/StickyBarTopSmall/StickyBarTopSmall.vue'
import UpgradeForLess from '~/scopes/product-core/components/presentationals//CustomizationFunnel/components/UpgradeForLess/UpgradeForLess.vue'
import Accessories from '~/scopes/product-core/components/presentationals/Accessories/Accessories.vue'
import CustomizationFunnel from '~/scopes/product-core/components/presentationals/CustomizationFunnel/CustomizationFunnel.vue'
import ProgressBar from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ProgressBar/ProgressBar.vue'
import FreeShippingBanner from '~/scopes/product-core/components/presentationals/FreeShippingBanner/FreeShippingBanner.vue'
import SellerComment from '~/scopes/product-core/components/presentationals/SellerComment/SellerComment.vue'
import { useProductTracking } from '~/scopes/product-core/composables/useFetch/useFetchProductTracking/composables/useProductTracking/useProductTracking'
import { STICKY_BARS_PORTAL_TARGET } from '~/scopes/product-core/constants'
import { useTelcoPicker } from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useTelcoPicker/useTelcoPicker'

const experiments = useExperiments()

const props = defineProps<{
  selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
  pickersResponse: GetPickersResponse
  pickersServices: GetLegacyServicePickersResponse | undefined
  isServicesPickersResponsePending: boolean
  price: Price
  priceWithoutSubsidies?: Price | null
  priceWithoutTradeIn?: Price | null
  product: GetProductResponse
  selectedMobilePlan?: MobilePlanOffer
  productTracking: ReturnType<typeof useProductTracking>
}>()

const { telcoCartSelection } = useTelcoPicker(() => props.product)

const shouldDisplayFreeShippingBanner = computed(() => {
  return experiments['experiment.ppFreeShippingBanner'] === 'withBanner'
})

const deal = computed(() => {
  if (experiments['experiment.flashSalesOnPDP'] !== 'enabled') return null

  return props.selectedOffer.deals?.[0] ?? null
})

const upgradeFields = ref<
  NonNullable<GetPickersResponse['upgrade']>['pickerFields'] | undefined
>(undefined)

function handleUpgradeAccepted(
  pickerFields?: NonNullable<GetPickersResponse['upgrade']>['pickerFields'],
) {
  upgradeFields.value = pickerFields
}

watch(
  () => props.pickersResponse,
  (_newValue, oldValue) => {
    // !oldValue.upgrade means that the previous selection was the upgraded one
    if (!oldValue.upgrade && upgradeFields.value) {
      upgradeFields.value = undefined
    }
  },
)
</script>
